/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Layout,
  Typography,
  Table,
  Tag,
  Row,
  Spin,
  Breadcrumb,
  Divider,
  Button,
  Select,
  Space,
  Alert,
} from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import {
  getDistributionPayoutsDetail,
  getDistributionPayoutFilters,
} from "../../api/distributionProject";
import { useDashboardScope } from "../../hooks/useDashboardScope";
import functions from "../../components/utils/functions";
import { netPayoutFromRevenue } from "../../components/utils/youtubePlatform";

const { Content } = Layout;
const { Title } = Typography;
const PAGE_SIZE = 20;

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "success", label: "Success" },
  { value: "initiated", label: "Initiated" },
  { value: "failed", label: "Failed" },
];

const STATUS_TAGS = {
  pending: { color: "warning", text: "Pending" },
  success: { color: "success", text: "Success" },
  initiated: { color: "processing", text: "Initiated" },
  failed: { color: "error", text: "Failed" },
};

const netPayoutAmount = (revenue, platformId, platformName) =>
  `₹${functions.formatIndianNumber(
    netPayoutFromRevenue(revenue, platformId, platformName),
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  )}`;

const PayoutsDetail = ({ auth_user }) => {
  const {
    effectiveUid,
    waitingForScope,
    isImpersonating,
    queryUserId,
    linkSearch,
  } = useDashboardScope(auth_user);

  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [platformOptions, setPlatformOptions] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [projectId, setProjectId] = useState(undefined);
  const [platformId, setPlatformId] = useState(undefined);
  const [status, setStatus] = useState(undefined);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (waitingForScope || !effectiveUid) return;
    setFiltersLoading(true);
    getDistributionPayoutFilters(effectiveUid)
      .then(({ platforms, projects }) => {
        setPlatformOptions(
          (platforms || []).map((p) => ({
            value: p.platform_id,
            label: p.platform_name || p.platform_id,
          }))
        );
        setProjectOptions(
          (projects || []).map((p) => ({
            value: p.project_id,
            label: p.title || p.project_id,
          }))
        );
      })
      .finally(() => setFiltersLoading(false));
  }, [effectiveUid, waitingForScope]);

  const filters = {
    project_id: projectId,
    platform_id: platformId,
    status,
  };

  const fetchPage = useCallback(
    async (pageNum = 1) => {
      if (waitingForScope || !effectiveUid) return;
      setLoading(true);
      try {
        const {
          payouts: list,
          total_count,
          error,
        } = await getDistributionPayoutsDetail(
          effectiveUid,
          pageNum,
          PAGE_SIZE,
          filters
        );
        setPayouts(list || []);
        setTotalCount(total_count);
        setPage(pageNum);
        setLoadError(error || null);
      } catch (_) {
        setPayouts([]);
        setTotalCount(0);
        setLoadError("Something went wrong while loading payouts.");
      } finally {
        setLoading(false);
      }
    },
    [effectiveUid, projectId, platformId, status, waitingForScope]
  );

  useEffect(() => {
    if (waitingForScope || !effectiveUid) return;
    fetchPage(1);
  }, [effectiveUid, projectId, platformId, status, waitingForScope, fetchPage]);

  const handleTableChange = (pag) => {
    if (pag.current !== page) {
      fetchPage(pag.current);
    }
  };

  const clearFilters = () => {
    setProjectId(undefined);
    setPlatformId(undefined);
    setStatus(undefined);
    setPage(1);
  };

  const columns = [
    {
      title: "Project",
      dataIndex: "project_title",
      key: "project_title",
      width: 200,
      render: (val, row) => val || row.distribution_project_id,
    },
    {
      title: "Month",
      dataIndex: "month_label",
      key: "month_label",
      width: 140,
    },
    {
      title: "Platform",
      dataIndex: "platform_name",
      key: "platform_name",
      width: 140,
      render: (name, row) => name || row.platform_id,
    },
    {
      title: "Net Revenue",
      dataIndex: "revenue",
      key: "revenue",
      width: 120,
      render: (revenue, row) =>
        netPayoutAmount(revenue, row.platform_id, row.platform_name),
    },
    {
      title: "Status",
      dataIndex: "revenue_status",
      key: "status",
      width: 110,
      render: (status) => {
        const info = STATUS_TAGS[status] || STATUS_TAGS.pending;
        return <Tag color={info.color}>{info.text}</Tag>;
      },
    },
  ];

  if (!auth_user?.user?.uid) {
    return (
      <Content style={{ padding: 24 }}>
        <p>Please sign in to view payout details.</p>
        <Link to="/">
          <Button icon={<ArrowLeftOutlined />}>Back to Dashboard</Button>
        </Link>
      </Content>
    );
  }

  if (waitingForScope || !effectiveUid) {
    return (
      <Layout style={{ minHeight: "100vh" }}>
        <Content style={{ padding: "0 24px 24px" }}>
          <div className="vc-spin-wrap" style={{ marginTop: 48 }}>
            <Spin tip="Loading…" />
          </div>
        </Content>
      </Layout>
    );
  }

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Content style={{ padding: "0 24px 24px" }}>
        <Divider />
        {isImpersonating && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Admin view"
            description={`Payout data for user_id ${queryUserId}.`}
          />
        )}
        <Breadcrumb style={{ marginBottom: 16 }}>
          <Breadcrumb.Item>
            <Link to={{ pathname: "/", search: linkSearch }}>Dashboard</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>Revenue Details</Breadcrumb.Item>
        </Breadcrumb>
        <Row
          align="middle"
          justify="space-between"
          style={{ marginBottom: 16 }}
        >
          <Title level={4} style={{ margin: 0 }}>
            Revenue Details
          </Title>
          <Link to={{ pathname: "/", search: linkSearch }}>
            <Button icon={<ArrowLeftOutlined />}>Back to Dashboard</Button>
          </Link>
        </Row>
        <Divider />
        <div className="vc-panel" style={{ padding: 24 }}>
          <Space wrap style={{ marginBottom: 16 }}>
            <Select
              placeholder="Project title"
              allowClear
              value={projectId}
              onChange={setProjectId}
              options={projectOptions}
              loading={filtersLoading}
              style={{ width: 240 }}
              showSearch
              optionFilterProp="label"
              filterOption={(input, opt) =>
                (opt?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
            />
            <Select
              placeholder="Platform"
              allowClear
              value={platformId}
              onChange={setPlatformId}
              options={platformOptions}
              loading={filtersLoading}
              style={{ width: 180 }}
            />
            <Select
              placeholder="Status"
              allowClear
              value={status}
              onChange={setStatus}
              options={STATUS_OPTIONS}
              style={{ width: 140 }}
            />
            <Button onClick={clearFilters}>Clear filters</Button>
          </Space>
          {loadError && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message="Could not load payouts"
              description={loadError}
              action={
                <Button size="small" onClick={() => fetchPage(page)}>
                  Try again
                </Button>
              }
            />
          )}
          {loading && payouts.length === 0 ? (
            <div className="vc-spin-wrap">
              <Spin tip="Loading payouts…" />
            </div>
          ) : (
            <Table
              dataSource={payouts}
              columns={columns}
              rowKey={(r) =>
                `${r.distribution_project_id}-${r.platform_id}-${r.month_date}-${r.revenue}`
              }
              loading={loading}
              pagination={{
                current: page,
                pageSize: PAGE_SIZE,
                total: totalCount,
                showSizeChanger: false,
                position: ["bottomRight"],
              }}
              onChange={handleTableChange}
              scroll={{ x: 700 }}
            />
          )}
          {!loadError &&
            !loading &&
            payouts.length === 0 &&
            totalCount === 0 && (
              <p>You do not have any revenue line items yet.</p>
            )}
        </div>
      </Content>
    </Layout>
  );
};

export default PayoutsDetail;
